import apiClient from 'api/apiClient';
import { ADMIN_REST_API_BASE_URL } from '../../../config';

export type AccountType = 'PERSONAL' | 'FAMILY' | 'BUSINESS';
export type DemoStatus = 'DRAFT' | 'GENERATING' | 'ACTIVE' | 'DISABLED' | 'ERROR';
export type ActivityVolume = 'LOW' | 'MEDIUM' | 'HIGH';
export type JobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface DemoPersonaDTO {
  id: number;
  name: string;
  accountType: AccountType;
  description: string | null;
  location: string | null;
  currency: string;
  income: number | null;
  incomeFrequency: 'WEEKLY' | 'MONTHLY' | 'ANNUAL';
  employmentType: string | null;
  ageMin: number | null;
  ageMax: number | null;
  householdSize: number | null;
  businessType: string | null;
  sector: string | null;
  defaultVolume: ActivityVolume;
  defaultHistoricalMonths: number;
  defaultFutureMonths: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DemoGenerationJobDTO {
  id: number;
  demoAccountId: number;
  status: JobStatus;
  triggeredBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  recordsCreated: number;
  createdAt: string;
}

export interface DemoAccountDTO {
  id: number;
  name: string;
  accountType: AccountType;
  status: DemoStatus;
  persona: DemoPersonaDTO | null;
  activityVolume: ActivityVolume;
  historicalMonths: number;
  futureMonths: number;
  generateRecurring: boolean;
  generateAdHoc: boolean;
  generateGoals: boolean;
  generateCoachInsights: boolean;
  enableBehaviourSimulation: boolean;
  lastGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userCount: number;
  latestJob: DemoGenerationJobDTO | null;
}

export interface DemoUserSummaryDTO {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  createdAt: string | null;
}

// ── Simulation types ──────────────────────────────────────────────────────────

export type ScenarioType =
  | 'NORMAL_MONTHLY_SPENDING'
  | 'INCREASED_DISCRETIONARY_SPENDING'
  | 'UNEXPECTED_LARGE_EXPENSE'
  | 'MISSED_DELAYED_INCOME'
  | 'ANNUAL_INSURANCE_RENEWAL'
  | 'NEW_RECURRING_SUBSCRIPTION'
  | 'INCREASED_SAVINGS_CONTRIBUTION'
  | 'GOAL_TARGET_DATE_CHANGE'
  | 'IRREGULAR_BUSINESS_REVENUE'
  | 'INCREASED_BUSINESS_OPERATING_COSTS';

export type SimulationStatus = 'IDLE' | 'RUNNING' | 'PAUSED' | 'COMPLETED';

export interface DemoSimulationProfileDTO {
  id: number;
  demoAccountId: number;
  scenarioType: ScenarioType;
  scenarioLabel: string;
  scenarioDescription: string;
  randomSeed: number | null;
  status: SimulationStatus;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SimulationRunResultDTO {
  profileId: number;
  scenarioType: string;
  scenarioLabel: string;
  recordsAffected: number;
  message: string;
  status: string;
}

// ── Coach types ───────────────────────────────────────────────────────────────

export interface InsightDTO {
  type: string;
  severity: 'INFO' | 'WARNING' | 'ALERT';
  category: string;
  title: string;
  summary: string;
  evidence: string | null;
  icon: string | null;
  actionLabel: string | null;
  actionTitle: string | null;
  actionDescription: string | null;
  estimatedMonthlySaving: number | null;
  amount: number | null;
  bucket: string | null;
  completed: boolean;
}

export interface ForecastResponse {
  year: number;
  month: number;
  monthlyIncome: number;
  daysElapsed: number;
  daysRemaining: number;
  needsBudget: number;
  wantsBudget: number;
  savingsBudget: number;
  spendToDateNeeds: number;
  spendToDateWants: number;
  spendToDateSavings: number;
  projectedNeeds: number;
  projectedWants: number;
  projectedSavings: number;
  projectedNet: number;
}

export interface DemoCoachInsightSummaryDTO {
  userId: number;
  email: string;
  fullName: string;
  insights: InsightDTO[];
  forecast: ForecastResponse | null;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface CreateDemoAccountRequest {
  name: string;
  accountType: AccountType;
  personaId?: number;
  activityVolume?: ActivityVolume;
  historicalMonths?: number;
  futureMonths?: number;
  generateRecurring?: boolean;
  generateAdHoc?: boolean;
  generateGoals?: boolean;
  generateCoachInsights?: boolean;
  enableBehaviourSimulation?: boolean;
}

export interface CreateDemoPersonaRequest {
  name: string;
  accountType: AccountType;
  description?: string;
  location?: string;
  currency?: string;
  income?: number;
  incomeFrequency?: 'WEEKLY' | 'MONTHLY' | 'ANNUAL';
  employmentType?: string;
  ageMin?: number;
  ageMax?: number;
  householdSize?: number;
  businessType?: string;
  sector?: string;
  defaultVolume?: ActivityVolume;
  defaultHistoricalMonths?: number;
  defaultFutureMonths?: number;
  active?: boolean;
}

const BASE = ADMIN_REST_API_BASE_URL;

// ── Demo Accounts ─────────────────────────────────────────────────────────────

export const getDemoAccounts = (
  page = 0,
  size = 20,
  accountType?: AccountType,
  status?: DemoStatus,
  activityVolume?: ActivityVolume,
) => {
  const params: Record<string, unknown> = { page, size };
  if (accountType) params.accountType = accountType;
  if (status) params.status = status;
  if (activityVolume) params.activityVolume = activityVolume;
  return apiClient.get<PageResponse<DemoAccountDTO>>(`${BASE}/demo-accounts`, { params });
};

export const getDemoAccount = (id: number) =>
  apiClient.get<DemoAccountDTO>(`${BASE}/demo-accounts/${id}`);

export const createDemoAccount = (data: CreateDemoAccountRequest) =>
  apiClient.post<DemoAccountDTO>(`${BASE}/demo-accounts`, data);

export const updateDemoAccount = (id: number, data: Partial<CreateDemoAccountRequest>) =>
  apiClient.put<DemoAccountDTO>(`${BASE}/demo-accounts/${id}`, data);

export const deleteDemoAccount = (id: number) =>
  apiClient.delete(`${BASE}/demo-accounts/${id}`);

export const duplicateDemoAccount = (id: number) =>
  apiClient.post<DemoAccountDTO>(`${BASE}/demo-accounts/${id}/duplicate`);

export const triggerGeneration = (id: number) =>
  apiClient.post<DemoGenerationJobDTO>(`${BASE}/demo-accounts/${id}/generate`);

export const getGenerationStatus = (id: number) =>
  apiClient.get<DemoGenerationJobDTO | { message: string }>(`${BASE}/demo-accounts/${id}/generate`);

export const resetDemoAccount = (id: number) =>
  apiClient.post(`${BASE}/demo-accounts/${id}/reset`);

export const enableDemoAccount = (id: number) =>
  apiClient.post(`${BASE}/demo-accounts/${id}/enable`);

export const disableDemoAccount = (id: number) =>
  apiClient.post(`${BASE}/demo-accounts/${id}/disable`);

export const listGenerationJobs = (id: number) =>
  apiClient.get<DemoGenerationJobDTO[]>(`${BASE}/demo-accounts/${id}/jobs`);

export const listDemoUsers = (id: number) =>
  apiClient.get<DemoUserSummaryDTO[]>(`${BASE}/demo-accounts/${id}/users`);

// ── Personas ─────────────────────────────────────────────────────────────────

export const getDemoPersonas = (activeOnly = false, accountType?: AccountType) => {
  const params: Record<string, unknown> = {};
  if (activeOnly) params.activeOnly = true;
  if (accountType) params.accountType = accountType;
  return apiClient.get<DemoPersonaDTO[]>(`${BASE}/demo-personas`, { params });
};

export const getDemoPersona = (id: number) =>
  apiClient.get<DemoPersonaDTO>(`${BASE}/demo-personas/${id}`);

export const createDemoPersona = (data: CreateDemoPersonaRequest) =>
  apiClient.post<DemoPersonaDTO>(`${BASE}/demo-personas`, data);

export const updateDemoPersona = (id: number, data: CreateDemoPersonaRequest) =>
  apiClient.put<DemoPersonaDTO>(`${BASE}/demo-personas/${id}`, data);

export const deleteDemoPersona = (id: number) =>
  apiClient.delete(`${BASE}/demo-personas/${id}`);

export const seedDefaultPersonas = () =>
  apiClient.post<{ message: string }>(`${BASE}/demo-personas/seed`);

// ── Simulation ────────────────────────────────────────────────────────────────

export const listSimulationProfiles = (accountId: number) =>
  apiClient.get<DemoSimulationProfileDTO[]>(`${BASE}/demo-accounts/${accountId}/simulations`);

export const createSimulationProfile = (accountId: number, scenarioType: ScenarioType, randomSeed?: number) =>
  apiClient.post<DemoSimulationProfileDTO>(`${BASE}/demo-accounts/${accountId}/simulations`, { scenarioType, randomSeed });

export const deleteSimulationProfile = (accountId: number, profileId: number) =>
  apiClient.delete(`${BASE}/demo-accounts/${accountId}/simulations/${profileId}`);

export const runSimulation = (accountId: number, profileId: number) =>
  apiClient.post<SimulationRunResultDTO>(`${BASE}/demo-accounts/${accountId}/simulations/${profileId}/run`);

export const revertSimulation = (accountId: number, profileId: number) =>
  apiClient.post<SimulationRunResultDTO>(`${BASE}/demo-accounts/${accountId}/simulations/${profileId}/revert`);

// ── Coach ─────────────────────────────────────────────────────────────────────

export const getDemoCoachInsights = (accountId: number) =>
  apiClient.get<DemoCoachInsightSummaryDTO[]>(`${BASE}/demo-accounts/${accountId}/coach/insights`);

// ── Integrity check ───────────────────────────────────────────────────────────

export interface IntegrityCheckItemDTO {
  checkId: string;
  label: string;
  status: 'PASS' | 'FAIL' | 'WARN' | 'SKIP';
  detail: string;
}

export interface DemoIntegrityReportDTO {
  accountId: number;
  userCount: number;
  checks: IntegrityCheckItemDTO[];
  passed: number;
  failed: number;
  warnings: number;
  skipped: number;
  allPassed: boolean;
}

export const getIntegrityCheck = (accountId: number) =>
  apiClient.get<DemoIntegrityReportDTO>(`${BASE}/demo-accounts/${accountId}/integrity-check`);

export const tickDemoAccount = (accountId: number) =>
  apiClient.post<{ transactionsAdded: number; message: string }>(`${BASE}/demo-accounts/${accountId}/tick`);
